import {
	type EntityAliasType,
	resolveEntityAliasExact,
} from "@/lib/entity-aliases";
import {
	BTC_ADDRESS_REGEX,
	EVM_ADDRESS_REGEX,
	PRINCIPAL_REGEX_FLEXIBLE,
	resolveDirectAddressEntityInput,
} from "@/lib/entity-resolution/shared-resolver";
import type {
	AddressMarkMatchedField,
	AddressMarkSearchMatch,
} from "@/lib/search/address-mark-search";

export type SearchIntent =
	| "empty"
	| "principal"
	| "btc-address"
	| "rune-id"
	| "keyword"
	| "unsupported-evm";

export interface SearchAnalysis {
	rawQuery: string;
	query: string;
	intent: SearchIntent;
}

export interface SearchTokenResult {
	id: string;
	name: string;
	ticker: string;
	price: number;
	holder_count?: number;
	comment_count?: number;
	verified?: boolean;
	featured?: boolean;
}

export interface SearchAddressResult {
	id: string;
	type?: string;
	entity?: {
		principal?: string;
		btc_deposit_address?: string | null;
		btc_wallet_address?: string | null;
		rune_deposit_address?: string | null;
	} | null;
}

export interface SearchDirectMatch {
	entityType: EntityAliasType | "address";
	source: "alias" | "address-mark";
	targetId: string;
	alias?: string;
	matchedField?: AddressMarkMatchedField;
	matchedValue?: string;
}

export interface UnifiedSearchState {
	analysis: SearchAnalysis;
	exactToken: SearchTokenResult | null;
	tokenResults: SearchTokenResult[];
	addressResult: SearchAddressResult | null;
	addressMarkResults: AddressMarkSearchMatch[];
	directMatch: SearchDirectMatch | null;
	errorMessage: string;
}

const runeIdRegex = /^[a-z0-9]{4}$/i;

interface TokenSearchResponse {
	data?: SearchTokenResult[];
}

interface AddressSearchResponse {
	data?: SearchAddressResult[];
}

export function normalizeSearchQuery(input: string) {
	if (typeof input !== "string") return "";

	return input
		.replace(/<script[\s\S]*?<\/script>/gi, "")
		.replace(/<[^>]*>/g, "")
		.replace(/[<>"]/g, "")
		.replace(/javascript:/gi, "")
		.replace(/vbscript:/gi, "")
		.replace(/\s+/g, " ")
		.trim()
		.slice(0, 100);
}

export function analyzeSearchQuery(input: string): SearchAnalysis {
	const query = normalizeSearchQuery(input);

	if (!query) {
		return { rawQuery: input, query, intent: "empty" };
	}

	if (EVM_ADDRESS_REGEX.test(query)) {
		return { rawQuery: input, query, intent: "unsupported-evm" };
	}

	if (PRINCIPAL_REGEX_FLEXIBLE.test(query)) {
		return { rawQuery: input, query, intent: "principal" };
	}

	if (BTC_ADDRESS_REGEX.test(query)) {
		return { rawQuery: input, query, intent: "btc-address" };
	}

	if (runeIdRegex.test(query)) {
		return { rawQuery: input, query, intent: "rune-id" };
	}

	return { rawQuery: input, query, intent: "keyword" };
}

function hasExactAddressMatch(
	entity: SearchAddressResult["entity"],
	query: string,
) {
	if (!entity) return false;
	const normalized = query.trim().toLowerCase();

	return (
		(entity.btc_wallet_address || "").toLowerCase() === normalized ||
		(entity.btc_deposit_address || "").toLowerCase() === normalized ||
		(entity.rune_deposit_address || "").toLowerCase() === normalized
	);
}

async function fetchExactToken(tokenId: string) {
	const response = await fetch(`https://api.odin.fun/v1/token/${tokenId}`);
	if (!response.ok) return null;
	return (await response.json()) as SearchTokenResult;
}

async function fetchTokenSearch(query: string, limit: number) {
	const response = await fetch(
		`https://api.odin.fun/v1/tokens?page=1&limit=${limit}&sort=price%3Adesc&search=${encodeURIComponent(query)}`,
	);

	if (!response.ok) {
		throw new Error(`Token search failed: ${response.status}`);
	}

	const data = (await response.json()) as TokenSearchResponse;
	return data.data || [];
}

async function fetchAddressSearch(query: string) {
	const response = await fetch(
		`https://api.odin.fun/v1/search?q=${encodeURIComponent(query)}`,
	);

	if (!response.ok) {
		throw new Error(`Address search failed: ${response.status}`);
	}

	const data = (await response.json()) as AddressSearchResponse;
	return data.data || [];
}

export async function performUnifiedSearch(
	input: string,
	labels?: { unsupportedEvm?: string; btcNotFound?: string },
): Promise<UnifiedSearchState> {
	const analysis = analyzeSearchQuery(input);

	const baseState: UnifiedSearchState = {
		analysis,
		exactToken: null,
		tokenResults: [],
		addressResult: null,
		addressMarkResults: [],
		directMatch: null,
		errorMessage: "",
	};

	if (analysis.intent === "empty") {
		return baseState;
	}

	if (analysis.intent === "unsupported-evm") {
		return {
			...baseState,
			errorMessage:
				labels?.unsupportedEvm || "暂不支持 EVM 地址，请输入 BTC 地址、身份 ID 或符文关键词。",
		};
	}

	if (analysis.intent === "principal") {
		return {
			...baseState,
			addressResult: {
				id: analysis.query,
				type: "principal",
				entity: { principal: analysis.query },
			},
		};
	}

	const directAddressEntity = await resolveDirectAddressEntityInput(analysis.query);
	if (directAddressEntity?.entityType === "address") {
		return {
			...baseState,
			addressResult: {
				id: directAddressEntity.entityId,
				type: directAddressEntity.source,
				entity: { principal: directAddressEntity.entityId },
			},
			directMatch:
				directAddressEntity.source === "alias-account"
					? {
							entityType: "account",
							source: "alias",
							alias: directAddressEntity.alias,
							targetId: directAddressEntity.entityId,
					  }
					: null,
		};
	}

	const aliasMatch = resolveEntityAliasExact(analysis.query);
	if (aliasMatch?.type === "account") {
		return {
			...baseState,
			addressResult: {
				id: aliasMatch.targetId,
				type: "alias-account",
				entity: { principal: aliasMatch.targetId },
			},
			directMatch: {
				entityType: "account",
				source: "alias",
				alias: aliasMatch.alias,
				targetId: aliasMatch.targetId,
			},
		};
	}

	if (analysis.intent === "btc-address") {
		const addressResults = await fetchAddressSearch(analysis.query);
		const matchedUser =
			addressResults.find(
				(item) => item.type === "user" && hasExactAddressMatch(item.entity, analysis.query),
			) || null;

		return {
			...baseState,
			addressResult: matchedUser,
			errorMessage: matchedUser
				? ""
				: labels?.btcNotFound || "未找到与该 BTC 地址精确匹配的用户。",
		};
	}

	if (analysis.intent === "rune-id") {
		const [exactToken, tokenResults] = await Promise.all([
			fetchExactToken(analysis.query.toLowerCase()),
			fetchTokenSearch(analysis.query, 8),
		]);

		return {
			...baseState,
			exactToken,
			tokenResults: tokenResults.filter((token) => token.id !== exactToken?.id),
		};
	}

	if (aliasMatch?.type === "token") {
		const exactToken = await fetchExactToken(aliasMatch.targetId);

		return {
			...baseState,
			exactToken,
			tokenResults: [],
			directMatch: {
				entityType: "token",
				source: "alias",
				alias: aliasMatch.alias,
				targetId: aliasMatch.targetId,
			},
		};
	}

	return {
		...baseState,
		tokenResults: await fetchTokenSearch(analysis.query, 10),
	};
}

export function mergeUnifiedSearchWithAddressMarks(
	state: UnifiedSearchState,
	addressMarkResults: AddressMarkSearchMatch[],
): UnifiedSearchState {
	if (addressMarkResults.length === 0) {
		return {
			...state,
			addressMarkResults: [],
		};
	}

	const directAddressMark =
		!state.addressResult && addressMarkResults.length === 1
			? addressMarkResults[0]
			: null;

	return {
		...state,
		addressMarkResults,
		addressResult:
			state.addressResult ||
			(directAddressMark
				? {
						id: directAddressMark.address,
						type: "address-mark",
						entity: { principal: directAddressMark.address },
					}
				: null),
		directMatch:
			state.directMatch ||
			(directAddressMark
				? {
						entityType: "address",
						source: "address-mark",
						targetId: directAddressMark.address,
						matchedField: directAddressMark.matchedField,
						matchedValue: directAddressMark.matchedValue,
					}
				: null),
	};
}
